/**
 * SmartEvaluate Playbook — Premium Consultant Guide
 * -----------------------------------------------------------------------------
 * An internal consulting methodology guide a PrimeS4 consultant uses during
 * discovery workshops and executive discussions. Content + UI only; no backend
 * logic, routes or navigation changes. Preserves the PrimeS4 design language
 * (white background, #025979 blue accents, Chivo headings, enterprise cards).
 */
import { useState } from "react";
import {
  Compass, Warehouse, RefreshCw, Cloud, Route as RouteIcon, Search, Layers,
  Gauge, SlidersHorizontal, TrendingUp, Map as MapIcon, Package, Sparkles,
  CheckCircle2, AlertTriangle, ShieldCheck, Quote, ArrowRight, ChevronDown,
  Target, LineChart, Cpu, ScrollText, Rocket, Lightbulb, FileText, Presentation,
  ClipboardList, Building2,
} from "lucide-react";

const BLUE = "#025979";

export default function GuidelinesPage() {
  return (
    <div data-testid="smartevaluate-playbook-page" className="max-w-5xl mx-auto pb-20">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-md border border-[#E5E7EB] bg-gradient-to-br from-[#F0F9FF] via-white to-white mb-8">
        <div className="absolute right-0 top-0 h-full w-40 bg-gradient-to-l from-[#E0F2FE]/70 to-transparent pointer-events-none" />
        <div className="relative px-7 py-8">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#025979] font-bold mb-2">
            PrimeS4 · Consulting Methodology
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0F172A] leading-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
            SmartEvaluate Playbook
          </h1>
          <p className="text-[13px] text-[#475569] mt-2 leading-relaxed max-w-2xl">
            A field guide for consultants — how to run discovery, evaluate options,
            justify recommendations and shape executive roadmaps. SmartEvaluate
            accelerates consulting judgement; it never replaces it.
          </p>
        </div>
      </div>

      {/* 1. Methodology */}
      <Section n="1" icon={Compass} title="SmartEvaluate Methodology"
        subtitle="What it is, when to use it, and what it produces">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <InfoCard icon={Target} title="Purpose" body="Frame the transformation strategy, compare SAP solution options and build a defensible, evidence-based recommendation and roadmap." />
          <InfoCard icon={Search} title="When to use" body="During discovery workshops, solution-fit evaluations and executive decision discussions — anytime a client asks “what should we do, and why?”" />
          <InfoCard icon={Package} title="Deliverables" body="Assessment summary, recommendations with confidence, ROI business case, executive roadmap and a presentation-ready report." />
        </div>
        <ConsultantNote>
          SmartEvaluate supports consultant decision-making — it does <strong>not</strong> replace
          consulting judgement. Every output is a well-evidenced starting point the consultant validates and owns.
        </ConsultantNote>
        <FlowStrip className="mt-5" testid="smartevaluate-methodology-workflow" steps={[
          { icon: Search, label: "Discover" },
          { icon: Gauge, label: "Evaluate" },
          { icon: Layers, label: "Recommend" },
          { icon: MapIcon, label: "Plan" },
          { icon: Rocket, label: "Execute" },
        ]} />
      </Section>

      {/* 2. Assessment Types */}
      <Section n="2" icon={Building2} title="Assessment Types"
        subtitle="Choose the right lens for the engagement">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <CompareCard icon={Warehouse} accent="#0E7490" title="Greenfield"
            rows={[
              ["Purpose", "New warehouse implementation."],
              ["Typical customer", "Building a new warehouse or greenfield distribution centre."],
              ["Typical outcome", "EWM Basic, EWM Advanced or LGM."],
            ]} testid="playbook-assessment-greenfield" />
          <CompareCard icon={RefreshCw} accent="#B45309" title="Brownfield"
            rows={[
              ["Purpose", "Modernize an existing warehouse."],
              ["Typical customer", "Existing SAP WM or legacy WMS customer."],
              ["Typical outcome", "Warehouse modernization roadmap."],
            ]} testid="playbook-assessment-brownfield" />
          <CompareCard icon={Cloud} accent={BLUE} title="Cloud Migration"
            rows={[
              ["Purpose", "Move SAP EWM to S/4HANA Private Cloud."],
              ["Typical customer", "Existing SAP EWM customer."],
              ["Typical outcome", "Technical, Selective or Full Transformation."],
            ]} testid="playbook-assessment-cloud_migration" />
        </div>
      </Section>

      {/* 3. Consultant Engagement Flow */}
      <Section n="3" icon={RouteIcon} title="Consultant Engagement Flow"
        subtitle="From first conversation to signed-off report">
        <Timeline testid="playbook-engagement-flow" steps={[
          ["Client Discovery", "Understand the business, drivers and current landscape."],
          ["Business Workshop", "Align stakeholders on objectives, scope and success criteria."],
          ["Assessment", "Capture structured inputs and run the SmartEvaluate evaluation."],
          ["Recommendation Review", "Validate the recommendation and confidence with the consultant lens."],
          ["Executive Roadmap", "Sequence the transformation into phases and quick wins."],
          ["Executive Presentation", "Present the business case and decision to leadership."],
          ["Final Executive Report", "Deliver the signed-off summary, ROI and roadmap."],
        ]} />
      </Section>

      {/* 4. Discovery Best Practices */}
      <Section n="4" icon={Search} title="Discovery Best Practices"
        subtitle="Discovery quality determines evaluation quality">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <CategoryCard icon={Target} title="Business Objectives" tips={["Lead with outcomes, not technology.", "Quantify cost, service and growth targets."]} />
          <CategoryCard icon={TrendingUp} title="Growth Plans" tips={["Capture volume and site expansion horizons.", "Stress-test the future operating model."]} />
          <CategoryCard icon={AlertTriangle} title="Operational Challenges" tips={["Document today's bottlenecks and pain.", "Separate symptoms from root causes."]} />
          <CategoryCard icon={Cpu} title="Technology Landscape" tips={["Map SAP WM / EWM / LGM and integrations.", "Note version, custom code and deployment."]} />
          <CategoryCard icon={RouteIcon} title="Automation" tips={["Identify ASRS, MFS and conveyor scope.", "Assess appetite for future automation."]} />
          <CategoryCard icon={ShieldCheck} title="Compliance" tips={["Capture regulatory and audit needs.", "Flag serialization / GxP requirements."]} />
          <CategoryCard icon={Lightbulb} title="Future Strategy" tips={["Understand the 3–5 year vision.", "Align the roadmap to strategic intent."]} />
        </div>
      </Section>

      {/* 5. Understanding Recommendations */}
      <Section n="5" icon={Layers} title="Understanding Recommendations"
        subtitle="The six outcomes SmartEvaluate can recommend">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <RecCard testid="playbook-rec-ewm_basic" title="EWM Basic" accent="#0E7490"
            purpose="Standard warehouse execution on S/4HANA EWM."
            when="Straightforward operations, limited automation."
            customer="Small-to-mid warehouses with standard processes."
            value="Modern platform with low complexity and fast time-to-value." />
          <RecCard testid="playbook-rec-ewm_advanced" title="EWM Advanced" accent={BLUE}
            purpose="Full EWM capability with advanced execution."
            when="Automation, MFS, labor management or complex flows."
            customer="High-throughput, automated or multi-process warehouses."
            value="Scales to the most demanding operations." />
          <RecCard testid="playbook-rec-lgm" title="Logistics Business Network / LGM" accent="#7C3AED"
            purpose="Lightweight, decentralized logistics management."
            when="Simpler sites or networked logistics scenarios."
            customer="Distributed operations not needing full EWM."
            value="Right-sized footprint with lower TCO." />
          <RecCard testid="playbook-rec-technical_migration" title="Technical Migration" accent="#0891B2"
            purpose="Infrastructure modernization to S/4HANA Private Cloud."
            when="Stable processes, primary need is platform modernization."
            customer="Existing EWM customers preserving current processes."
            value="Fast, low-risk move with a clean cloud foundation." />
          <RecCard testid="playbook-rec-selective_migration" title="Selective Migration" accent="#B45309"
            purpose="Modernization plus targeted process improvements."
            when="Moderate redesign with strong optimization ROI."
            customer="Customers ready to improve selected processes."
            value="Balanced value with limited disruption." />
          <RecCard testid="playbook-rec-full_transformation" title="Full Transformation" accent="#065F46"
            purpose="End-to-end warehouse transformation."
            when="Major redesign, automation or business transformation."
            customer="Customers pursuing a step-change in operations."
            value="Foundation for an AI-enabled, automated warehouse." />
        </div>
      </Section>

      {/* 6. Recommendation Confidence */}
      <Section n="6" icon={Gauge} title="Understanding Recommendation Confidence"
        subtitle="How strongly the evidence supports the recommendation">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ConfidenceCard testid="playbook-confidence-high" level="High" pct={92} color="#065F46"
            body="Strong evidence across the assessment. The recommendation can normally be presented confidently." />
          <ConfidenceCard testid="playbook-confidence-medium" level="Medium" pct={60} color="#B45309"
            body="Mixed evidence. Consultant validation is recommended before presenting." />
          <ConfidenceCard testid="playbook-confidence-low" level="Low" pct={28} color="#B91C1C"
            body="Limited information or conflicting inputs. Additional discovery is required." />
        </div>
        <ConsultantNote className="mt-4">
          Confidence uses these three levels only — do not introduce new levels. It reflects evidence strength, not the quality of the recommendation.
        </ConsultantNote>
      </Section>

      {/* 7. Override Guidance */}
      <Section n="7" icon={SlidersHorizontal} title="Override Guidance"
        subtitle="When the consultant's judgement takes precedence">
        <p className="text-[12.5px] text-[#374151] leading-relaxed mb-4">
          Override the recommendation when business context demands it. Common, legitimate drivers:
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          {["Business strategy", "Executive direction", "Industry requirements", "Budget constraints", "Special customer requirements"].map((t) => (
            <Chip key={t}>{t}</Chip>
          ))}
        </div>
        <Callout tone="blue" icon={ShieldCheck}>
          Every override is fully traceable — the reason and author are recorded in the audit trail, so decisions remain defensible.
        </Callout>
      </Section>

      {/* 8. ROI Interpretation */}
      <Section n="8" icon={TrendingUp} title="ROI Interpretation"
        subtitle="A directional business case — not a guarantee">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
          {[
            ["Investment", "Total programme cost to deliver the change."],
            ["Annual Benefit", "Recurring value once benefits are realized."],
            ["Payback", "Time for cumulative benefit to cover investment."],
            ["ROI", "Return relative to the investment over the horizon."],
            ["NPV", "Discounted value of the business case."],
            ["Risk Adjustment", "Benefits scaled by realization confidence."],
            ["Recommendation Confidence", "How strongly evidence backs the case."],
          ].map(([t, d]) => <MetricCard key={t} title={t} body={d} />)}
        </div>
        <FlowStrip testid="playbook-roi-flow" steps={[
          { icon: ClipboardList, label: "Investment" },
          { icon: LineChart, label: "Benefits" },
          { icon: ShieldCheck, label: "Risk Adjustment" },
          { icon: TrendingUp, label: "Business Case" },
        ]} />
      </Section>

      {/* 9. Executive Roadmap */}
      <Section n="9" icon={MapIcon} title="Executive Roadmap"
        subtitle="Sequence value while managing risk">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="playbook-roadmap">
          <PhaseCard step="Quick Wins" body="Low-effort, high-impact improvements that build momentum and fund the journey." accent="#0E7490" />
          <PhaseCard step="Phase 1" body="Core platform and foundational processes that unlock the target model." accent={BLUE} />
          <PhaseCard step="Phase 2" body="Advanced capabilities, automation and process optimization." accent="#7C3AED" />
          <PhaseCard step="Future State" body="Continuous improvement toward the AI-enabled, automated warehouse." accent="#065F46" />
        </div>
      </Section>

      {/* 10. Executive Deliverables */}
      <Section n="10" icon={FileText} title="Executive Deliverables"
        subtitle="What the client walks away with">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            [ClipboardList, "Assessment Summary", "A clear picture of the current state and captured inputs."],
            [Layers, "Recommendations", "The recommended solution(s) with rationale and confidence."],
            [MapIcon, "Implementation Plan", "The sequenced path from current to future state."],
            [TrendingUp, "ROI Analysis", "The directional business case and payback."],
            [FileText, "Executive Summary Report", "A leadership-ready narrative of the decision."],
            [Presentation, "Presentation Deck", "A polished deck to present the case to executives."],
          ].map(([Icon, t, d], i) => (
            <InfoCard key={t} icon={Icon} title={t} body={d} testid={`playbook-deliverable-${i}`} />
          ))}
        </div>
      </Section>

      {/* 11. Using PRIVA */}
      <Section n="11" icon={Sparkles} title="Using PRIVA"
        subtitle="Your on-demand SAP knowledge companion">
        <p className="text-[12.5px] text-[#374151] leading-relaxed mb-3">
          Ask PRIVA to explain concepts and terminology during workshops. Example questions:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="playbook-priva">
          {[
            "Explain Wave Management.",
            "Explain MFS Integration.",
            "Difference between EWM Basic and Advanced.",
            "When should LGM be recommended?",
            "Explain Decentralized EWM.",
            "Explain Selective Migration.",
            "Explain Full Transformation.",
          ].map((q) => (
            <div key={q} className="flex items-center gap-2 text-[12px] text-[#0F172A] bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3 py-2">
              <Sparkles className="w-3.5 h-3.5 text-[#025979] shrink-0" />
              <span>{q}</span>
            </div>
          ))}
        </div>
        <Callout tone="blue" icon={Lightbulb}>
          PRIVA provides knowledge and explanation. Consultants make recommendations.
        </Callout>
      </Section>

      {/* 12. Best Practices */}
      <Section n="12" icon={CheckCircle2} title="Best Practices">
        <CheckList tone="green" items={[
          "Business-first thinking — objectives drive the evaluation.",
          "Evidence-based recommendations grounded in validated inputs.",
          "Validate ROI assumptions before presenting numbers.",
          "Use executive language — outcomes, risks and decisions.",
          "Document overrides so decisions stay auditable.",
          "Review recommendations before presenting to the client.",
        ]} />
      </Section>

      {/* 13. Common Mistakes */}
      <Section n="13" icon={AlertTriangle} title="Common Mistakes">
        <CheckList tone="red" items={[
          "Starting with technology instead of business objectives.",
          "Ignoring future growth and expansion.",
          "Treating ROI as guaranteed savings.",
          "Blindly accepting recommendations without validation.",
          "Skipping executive validation.",
          "Incomplete discovery.",
        ]} />
      </Section>

      {/* 14. Governance */}
      <Section n="14" icon={ScrollText} title="Governance"
        subtitle="How evaluations stay controlled and traceable">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ["Assessment Versions", "Capture how the evaluation and scenarios evolve through the engagement."],
            ["Finalize", "Lock an agreed version as the source of truth for a decision."],
            ["Publishing", "Release the finalized assessment to the client-facing surfaces."],
            ["Client Portal", "The controlled space where clients view published outputs."],
            ["Audit Trail", "A complete, timestamped record of changes and decisions."],
            ["Override History", "Every consultant override, with reason and author."],
            ["Executive Approval", "The sign-off that promotes a recommendation to a decision."],
          ].map(([t, d]) => <InfoCard key={t} icon={ShieldCheck} title={t} body={d} />)}
        </div>
      </Section>

      {/* 15. Philosophy */}
      <section data-testid="smartevaluate-playbook-section-15"
        className="relative overflow-hidden rounded-md border border-[#025979]/25 bg-gradient-to-br from-[#0F2A38] to-[#025979] text-white px-8 py-9 mt-6">
        <Quote className="w-8 h-8 text-white/40 mb-3" />
        <p className="text-[15px] sm:text-[17px] leading-relaxed font-medium max-w-3xl" style={{ fontFamily: "Chivo, sans-serif" }}>
          SmartEvaluate accelerates consulting — it does not replace it. It combines
          SAP best practices, PrimeS4 implementation experience, structured assessment
          methodologies and consultant expertise to deliver consistent, evidence-based
          transformation recommendations.
        </p>
        <p className="mt-4 text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold">SmartEvaluate Philosophy</p>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Building blocks                                                     */
/* ------------------------------------------------------------------ */
function Section({ n, icon: Icon, title, subtitle, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section data-testid={`smartevaluate-playbook-section-${n}`}
      className="mb-6 bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden">
      <button type="button" onClick={() => setOpen((v) => !v)}
        data-testid={`smartevaluate-playbook-toggle-${n}`}
        className="w-full flex items-center gap-3 px-6 py-4 bg-[#F9FAFB] border-b border-[#E5E7EB] text-left hover:bg-[#F3F4F6] transition-colors">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#025979] text-white shrink-0">
          {Icon ? <Icon className="w-4.5 h-4.5" style={{ width: 18, height: 18 }} /> : <span className="text-[11px] font-bold">{n}</span>}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-bold text-[#025979]">{n}</span>
            <h2 className="text-[15px] font-bold text-[#0F172A] tracking-tight truncate" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</h2>
          </div>
          {subtitle && <p className="text-[11.5px] text-[#6B7280] mt-0.5 leading-snug">{subtitle}</p>}
        </div>
        <ChevronDown className={`w-4 h-4 text-[#9CA3AF] shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-6 py-5">{children}</div>}
    </section>
  );
}

function FlowStrip({ steps, className = "", testid }) {
  return (
    <div data-testid={testid} className={`flex flex-col sm:flex-row sm:items-stretch gap-2 ${className}`}>
      {steps.map((s, i) => (
        <div key={s.label} className="flex flex-col sm:flex-row sm:items-center sm:flex-1 gap-2">
          <div className="flex-1 flex items-center gap-3 rounded-sm border border-[#025979]/20 bg-gradient-to-br from-[#E0F2FE] to-[#F0F9FF] px-4 py-3">
            <s.icon className="w-4.5 h-4.5 text-[#025979] shrink-0" style={{ width: 18, height: 18 }} />
            <span className="text-[12.5px] font-semibold text-[#0F172A]">{s.label}</span>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="hidden sm:block w-4 h-4 text-[#94A3B8] shrink-0 rotate-0" />
          )}
        </div>
      ))}
    </div>
  );
}

function Timeline({ steps, testid }) {
  return (
    <ol data-testid={testid} className="relative border-l-2 border-[#E0F2FE] ml-3 space-y-5">
      {steps.map(([title, desc], i) => (
        <li key={title} className="relative pl-6">
          <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-[#025979] ring-4 ring-[#E0F2FE]" />
          <p className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
          <p className="text-[12px] text-[#475569] leading-relaxed mt-0.5">{desc}</p>
        </li>
      ))}
    </ol>
  );
}

function InfoCard({ icon: Icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-[#E5E7EB] bg-white p-4 hover:border-[#025979]/40 hover:shadow-sm transition-all">
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-[#E0F2FE]">
          <Icon className="w-4 h-4 text-[#025979]" />
        </span>
        <p className="text-[13px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <p className="text-[12px] text-[#475569] leading-relaxed">{body}</p>
    </div>
  );
}

function CompareCard({ icon: Icon, accent, title, rows, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-[#E5E7EB] bg-white overflow-hidden hover:shadow-md transition-shadow">
      <div className="px-4 py-3 flex items-center gap-2.5 border-b border-[#E5E7EB]" style={{ background: accent + "0F" }}>
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-md" style={{ background: accent + "1A" }}>
          <Icon className="w-4.5 h-4.5" style={{ color: accent, width: 18, height: 18 }} />
        </span>
        <p className="text-[14px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <div className="px-4 py-3 space-y-3">
        {rows.map(([l, v]) => (
          <div key={l}>
            <p className="text-[9.5px] uppercase tracking-wider font-bold text-[#94A3B8]">{l}</p>
            <p className="text-[12px] text-[#374151] leading-snug mt-0.5">{v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryCard({ icon: Icon, title, tips }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center gap-2 mb-2.5">
        <Icon className="w-4 h-4 text-[#025979]" />
        <p className="text-[12.5px] font-bold text-[#0F172A]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <ul className="space-y-1.5">
        {tips.map((t) => (
          <li key={t} className="flex items-start gap-2 text-[11.5px] text-[#475569] leading-relaxed">
            <span className="mt-1.5 w-1 h-1 rounded-full bg-[#025979] shrink-0" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RecCard({ title, accent, purpose, when, customer, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-[#E5E7EB] bg-white overflow-hidden hover:shadow-md transition-shadow flex flex-col">
      <div className="px-4 py-2.5 border-l-4" style={{ borderLeftColor: accent, background: accent + "0D" }}>
        <p className="text-[13.5px] font-bold text-[#0F172A] leading-snug" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      </div>
      <div className="px-4 py-3 space-y-2.5 text-[11.5px] leading-relaxed">
        <Field label="Purpose" value={purpose} />
        <Field label="When recommended" value={when} />
        <Field label="Typical customer" value={customer} />
        <Field label="Business value" value={value} accent={accent} />
      </div>
    </div>
  );
}

function Field({ label, value, accent }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider font-bold" style={{ color: accent || "#94A3B8" }}>{label}</p>
      <p className="text-[#374151] mt-0.5">{value}</p>
    </div>
  );
}

function ConfidenceCard({ level, pct, color, body, testid }) {
  return (
    <div data-testid={testid} className="rounded-md border border-[#E5E7EB] bg-white p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-bold uppercase tracking-wide" style={{ color, fontFamily: "Chivo, sans-serif" }}>{level}</span>
        <span className="text-[11px] font-bold" style={{ color }}>{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-[#F1F5F9] overflow-hidden mb-3">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <p className="text-[12px] text-[#475569] leading-relaxed">{body}</p>
    </div>
  );
}

function MetricCard({ title, body }) {
  return (
    <div className="rounded-sm border border-[#E5E7EB] bg-white p-3">
      <p className="text-[12px] font-bold text-[#025979]" style={{ fontFamily: "Chivo, sans-serif" }}>{title}</p>
      <p className="text-[11px] text-[#475569] leading-relaxed mt-1">{body}</p>
    </div>
  );
}

function PhaseCard({ step, body, accent }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white overflow-hidden">
      <div className="px-3 py-2 text-white text-[12px] font-bold" style={{ background: accent, fontFamily: "Chivo, sans-serif" }}>{step}</div>
      <p className="px-3 py-3 text-[11.5px] text-[#475569] leading-relaxed">{body}</p>
    </div>
  );
}

function CheckList({ items, tone }) {
  const Icon = tone === "red" ? AlertTriangle : CheckCircle2;
  const color = tone === "red" ? "#B91C1C" : "#065F46";
  return (
    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5">
      {items.map((t) => (
        <li key={t} className="flex items-start gap-2 text-[12.5px] text-[#1F2937] leading-relaxed">
          <Icon className="w-4 h-4 shrink-0 mt-0.5" style={{ color }} />
          <span>{t}</span>
        </li>
      ))}
    </ul>
  );
}

function ConsultantNote({ children, className = "" }) {
  return (
    <div className={`rounded-sm border-l-4 border-[#025979] bg-[#F0F9FF] px-4 py-3 ${className}`}>
      <p className="text-[9.5px] uppercase tracking-wider font-bold text-[#025979] mb-1">Consultant Note</p>
      <p className="text-[12px] text-[#334155] leading-relaxed">{children}</p>
    </div>
  );
}

const CALLOUT_TONES = {
  amber: "bg-[#FFFBEB] border-[#FCD34D]/60 text-[#78350F]",
  blue: "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E3A8A]",
};

function Callout({ tone = "blue", icon: Icon, children }) {
  return (
    <div className={`flex items-start gap-2.5 rounded-sm border px-4 py-3 text-[12px] leading-relaxed ${CALLOUT_TONES[tone]}`}>
      {Icon && <Icon className="w-4 h-4 shrink-0 mt-0.5" />}
      <span>{children}</span>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#E0F2FE] text-[#025979] border border-[#025979]/20 text-[11.5px] font-medium">
      {children}
    </span>
  );
}
